import { useEffect, useRef, ComponentType } from 'react';

/**
 * Tracks currently visible (mounted) connected components and their props.
 *
 * This module provides functionality to maintain a registry of components
 * that are currently rendered, along with their current props. This is
 * useful for debugging, development tools, or other introspection needs.
 */

// Track currently visible (mounted) connected components.
// We use a Map so we can reliably update and delete by a stable id.
type VisibleComponentRecord = { props: any };
const visibleComponents = new Map<string, VisibleComponentRecord>();

let instanceCount = 0;
const getNextInstanceName = () => `view-${instanceCount++}`;

/**
 * Gets a snapshot of all currently visible components and their props.
 *
 * @returns Array of objects containing component id and props
 */
export const getVisibleComponents = () =>
  Array.from(visibleComponents.entries()).map(([id, { props }]) => ({
    id,
    props,
  }));

/**
 * Registers a component as visible with its current props.
 * Should be called when a component mounts or its props change.
 *
 * @param id Unique identifier for the component instance
 * @param props Current props of the component
 */
export const registerVisibleComponent = (id: string, props: any): void => {
  visibleComponents.set(id, { props: { ...props } });
};

/**
 * Unregisters a component from the visible components list.
 * Should be called when a component unmounts.
 *
 * @param id Unique identifier for the component instance
 */
export const unregisterVisibleComponent = (id: string): void => {
  visibleComponents.delete(id);
};

/**
 * Custom hook for tracking a connected component's visibility and props.
 * Handles ID generation, display name creation, and lifecycle management.
 *
 * @param Component The component being tracked
 * @param props The current props of the component
 */
export const useVisibleComponentTracking = <Props extends object>(
  Component: ComponentType<Props>,
  props: Props
): void => {
  const baseName = Component.displayName || Component.name;
  const idRef = useRef<string | null>(null);

  if (!idRef.current) {
    const suffix = getNextInstanceName();
    idRef.current = `${baseName}#${suffix}`;
  }

  useEffect(() => {
    const id = idRef.current!;
    registerVisibleComponent(id, props);
    return () => {
      unregisterVisibleComponent(id);
    };
  });
};
